import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import drEasyApi from "../Api/Customapi";
import React from "react";

// API Service Functions
const invoiceApi = {
  getUserAccount: async (params) => {
    console.log(params);

    const response = await drEasyApi.get("/api/Invoice/GetUserAccount", {
      params: {
        UserId: params.userId,
        PageNumber: params.pageNumber || 1,
        PageSize: params.pageSize || 10,
        IsPaid: params.isPaid,
        FromDate: params.fromDate,
        ToDate: params.toDate,
        SortBy: params.sortBy,
        SortDescending: params.sortDescending,
      },
    });
    return response.data;
  },

  // Get single invoice by ID
  getInvoice: async (invoiceId) => {
    const response = await drEasyApi.get("/api/Invoice/Get", {
      params: {
        InvoiceId: invoiceId,
      },
    });
    return response.data;
  },

  editInvoice: async (data) => {
    const response = await drEasyApi.put("/api/Invoice/Edit", {
      invoiceId: data.invoiceId,
      visitPrice: data.visitPrice,
      discountAmount: data.discountAmount,
      discountPer: data.discountPer,
      paidAmount: data.paidAmount,
    });
    return response.data;
  },

  // Pay invoice
  payInvoice: async (data) => {
    const response = await drEasyApi.post("/api/Invoice/Pay", {
      invoiceId: data.invoiceId,
      paidAmount: data.paidAmount,
    });
    return response.data;
  },

  // Edit invoice service
  editInvoiceService: async (data) => {
    const response = await drEasyApi.put("/api/Invoice/EditService", {
      invoiceId: data.invoiceId,
      invoiceServiceId: data.invoiceServiceId,
      quantity: data.quantity,
      unitPrice: data.unitPrice,
    });
    return response.data;
  },

  // Remove invoice service
  removeInvoiceService: async (invoiceServiceId) => {
    const response = await drEasyApi.delete("/api/Invoice/RemoveService", {
      params: { InvoiceServiceId: invoiceServiceId },
    });
    return response.data;
  },
};

// Hook for getting user account/invoices
export const useUserAccount = (params, options = {}) => {
  return useQuery({
    queryKey: ["userAccount", params],
    queryFn: () => invoiceApi.getUserAccount(params),
    enabled: !!params.userId,
    staleTime: 30000, // 30 seconds
    ...options,
  });
};

// Hook for getting a single invoice
export const useInvoice = (invoiceId, options = {}) => {
  return useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => invoiceApi.getInvoice(invoiceId),
    enabled: !!invoiceId,
    staleTime: 30000, // 30 seconds
    ...options,
  });
};

// Hook for invoice mutations
export const useInvoiceMutations = () => {
  const queryClient = useQueryClient();

  // Edit Invoice Mutation
  const editInvoiceMutation = useMutation({
    mutationFn: invoiceApi.editInvoice,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["userAccount"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({
        queryKey: ["invoice", variables.invoiceId],
      });
      message.success("Invoice updated successfully");
    },
    onError: (error) => {
      console.error("Error editing invoice:", error);
      message.error(error.message || "Failed to update invoice");
    },
  });

  // Pay Invoice Mutation
  const payInvoiceMutation = useMutation({
    mutationFn: invoiceApi.payInvoice,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["userAccount"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({
        queryKey: ["invoice", variables.invoiceId],
      });
      message.success("Payment recorded successfully");
    },
    onError: (error) => {
      console.error("Error paying invoice:", error);
      message.error(error.message || "Failed to record payment");
    },
  });

  // Edit Invoice Service Mutation
  const editInvoiceServiceMutation = useMutation({
    mutationFn: invoiceApi.editInvoiceService,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["userAccount"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({
        queryKey: ["invoice", variables.invoiceId],
      });
      message.success("Service updated successfully");
    },
    onError: (error) => {
      console.error("Error editing invoice service:", error);
      message.error(error.message || "Failed to update service");
    },
  });

  // Remove Invoice Service Mutation
  const removeInvoiceServiceMutation = useMutation({
    mutationFn: invoiceApi.removeInvoiceService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userAccount"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      message.success("Service removed successfully");
    },
    onError: (error) => {
      console.error("Error removing invoice service:", error);
      message.error(error.message || "Failed to remove service");
    },
  });

  return {
    editInvoice: editInvoiceMutation,
    payInvoice: payInvoiceMutation,
    editInvoiceService: editInvoiceServiceMutation,
    removeInvoiceService: removeInvoiceServiceMutation,
  };
};

// Combined hook for complete invoice management
export const useInvoiceManagement = (userId, filters = {}) => {
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  });

  const accountQuery = useUserAccount({
    userId,
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...filters,
  });

  const mutations = useInvoiceMutations();

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  return {
    invoices: accountQuery.data?.items || [],
    totalCount: accountQuery.data?.totalCount || 0,
    loading: accountQuery.isLoading,
    error: accountQuery.error,
    pagination: {
      ...pagination,
      total: accountQuery.data?.totalCount || 0,
    },
    handleTableChange,
    refetch: accountQuery.refetch,
    ...mutations,
  };
};

export { invoiceApi };
